/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import {
    array, bool, func, object,
} from 'prop-types';

import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useGetAccessTokenSafely from '../../../../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../../../../gql';

import findRecentlyViewedProducts from '../../../../../gql/queries/findRecentlyViewedProducts';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useSalesforceProductRecommendationQuery from '../../../../../helpers/Personalization/useSalesforceProductRecommendationQuery';
import PLARecsBuilder from './PLARecsBuilder';
import useBrowserUUID from '../../../../../helpers/useBrowserUUID';
import noop from '../../../../../helpers/noop';

export default function PLARecsContainer({
    product, skuData, isPlaRedesignEnabled, cmsRecBlockData, isCategoryBasedQuery, isFromRVOnPLA, rvProducts, campaignTrackClickThrough,
}) {
    const userAttributes = [];
    if (isCategoryBasedQuery) {
        // 6810; this category is from agg req, ultimately from PIM
        const productCategory = product?.categories?.[0];
        userAttributes.push({ name: 'sessionReferenceCategory', value: productCategory });
    }

    const isSympathySku = skuData?.some((sku) => sku?.sympathyValue === '1' || sku?.sympathyValue === '2');

    const {
        loading, error, data, variables,
    } = useSalesforceProductRecommendationQuery({
        queryName: 'PLARecsContainer',
        campaignFeature: cmsRecBlockData?.pz_campaign_id,
        productName: product?.name,
        partNumber: product?.partNumber,
        isSympathySku,
        targeting: {
            pageType: 'product',
        },
        filterByZipCode: true,
        optInHoldoutParticipation: true,
        resolveIdentity: true,
        addUserAttributes: userAttributes?.length ? userAttributes : [],
    });

    if (loading || variables?.skip) return null;

    if (error) {
        mbpLogger.logError({
            function: 'PLARecsContainer.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PLA',
            jsError: error,
        });
    }

    if (data?.productRecommendationsSFMulti?.length > 0 && isFromRVOnPLA) {
        return (
            <PLARecsBuilder
                data={data}
                products={rvProducts}
            />
        );
    }

    if (data?.productRecommendationsSFMulti?.length > 0 && data.productRecommendationsSFMulti[0]?.products) {
        // non recently viewed features
        const products = data.productRecommendationsSFMulti[0]?.products;
        if (products?.length > 0) {
            return (
                <PLARecsBuilder
                    data={data}
                    products={products}
                    campaignTrackClickThrough={campaignTrackClickThrough}
                />
            );
        }
    }

    return null;
}

PLARecsContainer.propTypes = {
    product: object.isRequired,
    skuData: array.isRequired,
    isPlaRedesignEnabled: bool.isRequired,
    cmsRecBlockData: object,
    isCategoryBasedQuery: bool,
    isFromRVOnPLA: bool,
    rvProducts: array,
    campaignTrackClickThrough: func,
};

PLARecsContainer.defaultProps = {
    cmsRecBlockData: {},
    isCategoryBasedQuery: false,
    isFromRVOnPLA: false,
    rvProducts: [],
    campaignTrackClickThrough: noop,
};
